<template>
  <div class="asset-inventory-audit-container">
    <ds-page-header :title="$t('assetInventoryAudit.page.title')" :subtitle="$t('assetInventoryAudit.page.subtitle')">
      <template #extra>
        <el-button size="small" icon="el-icon-back" @click="_goBack">{{ $t('common.back') }}</el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventoryAudit.inventoryInfo') }}</span>
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryAudit.inventoryName') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryAuditInfo.name || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryAudit.inventoryPerson') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryAuditInfo.staffName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryAudit.warehouse') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryAuditInfo.shName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryAudit.inventoryDate') }}</span>
          <span class="ds-info-grid__value ds-num">{{ assetInventoryAuditInfo.invTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('assetInventoryAudit.remark') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryAuditInfo.remark || '-' }}</span>
        </div>
      </div>
    </div>

    <div class="ds-panel">
      <ds-toolbar :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left><span class="ds-panel__title">{{ $t('assetInventoryAudit.inventoryProducts') }}</span></template>
      </ds-toolbar>
      <ds-data-table :data="assetInventoryAuditInfo.resourceStores" border
        :empty-description="$t('assetInventoryAudit.page.empty')">
        <el-table-column :label="$t('assetInventoryAudit.productType')" align="center" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.parentRstName ? scope.row.parentRstName : '-' }} &gt; {{ scope.row.rstName ? scope.row.rstName : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="resName" :label="$t('assetInventoryAudit.productName')" align="center" min-width="140" />
        <el-table-column :label="$t('assetInventoryAudit.specification')" align="center" width="110">
          <template slot-scope="scope">{{ scope.row.rssName ? scope.row.rssName : '-' }}</template>
        </el-table-column>
        <el-table-column prop="resCode" :label="$t('assetInventoryAudit.productCode')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.resCode || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="isFixedName" :label="$t('assetInventoryAudit.isFixed')" align="center" width="110" />
        <el-table-column prop="price" :label="$t('assetInventoryAudit.referencePrice')" align="center" width="110">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.price || '-' }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryAudit.originalStock')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.originalStock }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryAudit.afterInventory')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.quantity }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('assetInventoryAudit.remark')" align="center" min-width="140" />
      </ds-data-table>
    </div>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventoryAudit.auditOpinion') }}</span>
      </div>
      <el-input type="textarea" :rows="4" v-model="assetInventoryAuditInfo.opinion"
        :placeholder="$t('assetInventoryAudit.opinionPlaceholder')" maxlength="250" show-word-limit />
      <div class="ds-audit-actions">
        <el-button type="primary" @click="_doAssetInventory('4000')">
          <i class="el-icon-check"></i>{{ $t('assetInventoryAudit.approve') }}
        </el-button>
        <el-button type="warning" @click="_doAssetInventory('3000')">
          <i class="el-icon-close"></i>{{ $t('assetInventoryAudit.reject') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAssetInventoryDetail, updateAssetInventory, getAssetInventoryProduct } from '@/api/resource/assetInventoryAuditApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'

export default {
  name: 'AssetInventoryAuditList',
  components: { DsPageHeader, DsToolbar, DsDataTable },
  data() {
    return {
      assetInventoryAuditInfo: {
        aiId: '', shId: '', shName: '', name: '', staffName: '', invTime: '',
        shopHouses: [], resourceStores: [], state: '2000', remark: '', opinion: '',
        communityId: getCommunityId()
      }
    }
  },
  created() {
    this.assetInventoryAuditInfo.aiId = this.$route.query.aiId
    this._queryAssetInventory()
    this.queryAssetInventoryProduct()
  },
  methods: {
    async _doAssetInventory(state) {
      if (!this.assetInventoryAuditInfo.opinion) {
        this.$message.error(this.$t('assetInventoryAudit.opinionRequired'))
        return
      }
      this.assetInventoryAuditInfo.state = state
      try {
        const res = await updateAssetInventory(this.assetInventoryAuditInfo)
        if (res.code === 0) {
          this.$message.success(res.msg)
          this._goBack()
        } else {
          this.$message.error(res.msg)
        }
      } catch (error) {
        this.$message.error(this.$t('common.requestFailed'))
      }
    },
    _goBack() {
      this.$router.go(-1)
    },
    async _queryAssetInventory() {
      try {
        const params = { page: 1, row: 1, aiId: this.assetInventoryAuditInfo.aiId, shopId: this.assetInventoryAuditInfo.communityId }
        const res = await getAssetInventoryDetail(params)
        if (res.data && res.data.length > 0) {
          Object.assign(this.assetInventoryAuditInfo, res.data[0])
        }
      } catch (error) {
        console.error('请求失败:', error)
      }
    },
    async queryAssetInventoryProduct() {
      try {
        const params = { page: 1, row: 100, applyOrderId: this.assetInventoryAuditInfo.aiId }
        const res = await getAssetInventoryProduct(params)
        if (res.data) {
          res.data.forEach(item => { this._computeData(item) })
          this.assetInventoryAuditInfo.resourceStores = res.data
        }
      } catch (error) {
        console.error('请求失败:', error)
      }
    },
    _computeData(item) {
      item.invProfit = parseInt(item.invQuantity) - parseInt(item.quantity)
      if (item.invProfit < 0) item.invProfit = 0
      item.invLoss = parseInt(item.quantity) - parseInt(item.invQuantity)
      if (item.invLoss < 0) item.invLoss = 0
      item.invProfitMoney = item.invProfit * parseFloat(item.price)
      item.invLossMoney = item.invLoss * parseFloat(item.price)
    }
  }
}
</script>

<style lang="scss" scoped>
.asset-inventory-audit-container { padding: var(--ds-spacing-lg); background-color: var(--ds-gray-100); min-height: calc(100vh - 60px); }
.ds-panel { background: #fff; border: 1px solid var(--ds-gray-300); border-radius: var(--ds-radius-md); box-shadow: var(--ds-shadow-sm); overflow: hidden; margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header { display: flex; align-items: center; justify-content: space-between; padding-bottom: var(--ds-spacing-md); margin-bottom: var(--ds-spacing-md); border-bottom: 1px solid var(--ds-gray-200); }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); } }
.ds-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; &--wide { grid-column: 1 / -1; } }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; } }
.ds-audit-actions { margin-top: var(--ds-spacing-lg); text-align: center; }
</style>
